import { API_BASE_URL } from '../config/api';

export interface TenantPaymentMethod {
  id: string;
  type: 'CREDIT_CARD' | 'DEBIT_CARD' | 'BANK_ACCOUNT' | 'MPESA' | 'PAYPAL' | 'OTHER';
  provider: string;
  lastFour: string;
  expiryMonth?: number;
  expiryYear?: number;
  holderName: string;
  isDefault: boolean;
  isActive: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface TenantSecuritySettings {
  id: string;
  tenantId: string;
  twoFactorEnabled: boolean;
  twoFactorSecret?: string;
  backupCodes: string[];
  loginAlerts: boolean;
  suspiciousActivityAlerts: boolean;
  deviceTrustDuration: number;
  maxConcurrentSessions: number;
  sessionTimeout: number;
  requireStrongPassword: boolean;
  passwordExpiryDays?: number;
  recoveryEmail?: string;
  recoveryPhone?: string;
  securityQuestions?: any;
  createdAt: string;
  updatedAt: string;
}

export interface TenantNotificationSettings {
  id: string;
  tenantId: string;
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  rentReminders: boolean;
  paymentConfirmations: boolean;
  maintenanceUpdates: boolean;
  emergencyAlerts: boolean;
  generalAnnouncements: boolean;
  leaseReminders: boolean;
  lateFeeNotifications: boolean;
  preferredTime: number;
  preferredDays: number[];
  doNotDisturbStart: number;
  doNotDisturbEnd: number;
  rentReminderChannel: 'SMS' | 'WHATSAPP' | 'EMAIL';
  paymentChannel: 'SMS' | 'WHATSAPP' | 'EMAIL';
  maintenanceChannel: 'SMS' | 'WHATSAPP' | 'EMAIL';
  emergencyChannel: 'SMS' | 'WHATSAPP' | 'EMAIL';
  rentReminderFrequency: number;
  overdueReminderFrequency: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantSystemPreferences {
  id: string;
  tenantId: string;
  language: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  currency: string;
  numberFormat: string;
  theme: string;
  fontSize: string;
  compactMode: boolean;
  defaultView: string;
  showTutorials: boolean;
  showTips: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantPrivacySettings {
  id: string;
  tenantId: string;
  shareDataWithLandlord: boolean;
  shareDataWithAgents: boolean;
  shareDataWithCaretakers: boolean;
  allowAnalytics: boolean;
  allowUsageTracking: boolean;
  allowPersonalization: boolean;
  profileVisibility: 'PUBLIC' | 'PRIVATE' | 'FRIENDS_ONLY';
  showContactInfo: boolean;
  showLeaseInfo: boolean;
  dataRetentionPeriod: number;
  autoDeleteOldData: boolean;
  allowMarketing: boolean;
  allowPromotional: boolean;
  allowThirdParty: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantMaintenancePreferences {
  id: string;
  tenantId: string;
  preferredDays: number[];
  preferredStartTime: number;
  preferredEndTime: number;
  contactMethod: string;
  allowWeekendContact: boolean;
  allowEveningContact: boolean;
  allowEmergencyAccess: boolean;
  allowScheduledAccess: boolean;
  requireAdvanceNotice: number;
  maintenanceUpdates: boolean;
  photoUpdates: boolean;
  completionNotifications: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantActivityLog {
  id: string;
  tenantId: string;
  action: string;
  description: string;
  category: 'AUTHENTICATION' | 'SETTINGS' | 'PAYMENT' | 'MAINTENANCE' | 'PROFILE' | 'SECURITY' | 'SYSTEM';
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  ipAddress?: string;
  userAgent?: string;
  deviceInfo?: any;
  location?: any;
  metadata?: any;
  createdAt: string;
}

export interface TenantSettings {
  paymentMethods: TenantPaymentMethod[];
  securitySettings?: TenantSecuritySettings;
  notificationSettings?: TenantNotificationSettings;
  systemPreferences?: TenantSystemPreferences;
  privacySettings?: TenantPrivacySettings;
  maintenancePreferences?: TenantMaintenancePreferences;
  recentActivity: TenantActivityLog[];
}

class TenantSettingsService {
  private baseUrl = `${API_BASE_URL}/tenant-settings`;

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Get Firebase token – tenant may be in tenantAuth (email/password) or main auth (Google sign-in)
    let token: string | null = null;
    try {
      const { auth, tenantAuth } = await import('../config/firebase');
      const currentUser = tenantAuth.currentUser || auth.currentUser;
      if (currentUser) {
        token = await currentUser.getIdToken();
      }
    } catch (error) {
      console.error('Failed to get Firebase token:', error);
    }
    
    if (!token) {
      throw new Error('Not authenticated. Please log in.');
    }
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  }

  // Get all tenant settings
  async getSettings(tenantId: string): Promise<TenantSettings> {
    return this.request<TenantSettings>(`/${tenantId}`);
  }

  // Payment Methods
  async addPaymentMethod(tenantId: string, paymentMethod: Partial<TenantPaymentMethod>): Promise<TenantPaymentMethod> {
    return this.request<TenantPaymentMethod>(`/${tenantId}/payment-methods`, {
      method: 'POST',
      body: JSON.stringify(paymentMethod),
    });
  }

  async updatePaymentMethod(tenantId: string, methodId: string, updates: Partial<TenantPaymentMethod>): Promise<TenantPaymentMethod> {
    return this.request<TenantPaymentMethod>(`/${tenantId}/payment-methods/${methodId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePaymentMethod(tenantId: string, methodId: string): Promise<void> {
    return this.request<void>(`/${tenantId}/payment-methods/${methodId}`, {
      method: 'DELETE',
    });
  }

  // Security Settings
  async updateSecuritySettings(tenantId: string, settings: Partial<TenantSecuritySettings>): Promise<TenantSecuritySettings> {
    return this.request<TenantSecuritySettings>(`/${tenantId}/security`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Notification Settings
  async updateNotificationSettings(tenantId: string, settings: Partial<TenantNotificationSettings>): Promise<TenantNotificationSettings> {
    return this.request<TenantNotificationSettings>(`/${tenantId}/notifications`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // System Preferences
  async updateSystemPreferences(tenantId: string, preferences: Partial<TenantSystemPreferences>): Promise<TenantSystemPreferences> {
    return this.request<TenantSystemPreferences>(`/${tenantId}/preferences`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Privacy Settings
  async updatePrivacySettings(tenantId: string, settings: Partial<TenantPrivacySettings>): Promise<TenantPrivacySettings> {
    return this.request<TenantPrivacySettings>(`/${tenantId}/privacy`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Maintenance Preferences
  async updateMaintenancePreferences(tenantId: string, preferences: Partial<TenantMaintenancePreferences>): Promise<TenantMaintenancePreferences> {
    return this.request<TenantMaintenancePreferences>(`/${tenantId}/maintenance`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Activity Log
  async getActivityLog(tenantId: string, page = 1, limit = 20, category?: string): Promise<{
    activities: TenantActivityLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(category && { category }),
    });

    return this.request<{
      activities: TenantActivityLog[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(`/${tenantId}/activity?${params}`);
  }
}

export const tenantSettingsService = new TenantSettingsService();
